import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaShare, FaUsers, FaCopy, FaCheck, FaUserPlus, 
  FaSpinner, FaChartLine, FaClock, FaCheckCircle
} from 'react-icons/fa';

const API = '/api';

export function ProfessionalReferralCard({ sessionToken }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${API}/professionals/referral/stats`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setReferralData(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Only approved professionals can access referrals');
      } else {
        setError('Unable to load referral data');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateReferralCode = async () => {
    setGenerating(true);
    try {
      const response = await axios.get(`${API}/professionals/referral/my-code`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setReferralData({
        has_referral_code: true,
        ...response.data,
        referred_professionals: []
      });
    } catch (err) {
      setError('Unable to generate referral code');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-center py-8">
          <FaSpinner className="animate-spin text-2xl text-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="text-center py-4">
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // No referral code yet
  if (!referralData?.has_referral_code) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaUserPlus className="text-blue-600 text-2xl" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Refer Your Colleagues</h3>
          <p className="text-gray-600 mb-6">
            Know other professionals who would be a great fit? Generate your unique referral link 
            and help grow our trusted network.
          </p>
          <button
            onClick={generateReferralCode}
            disabled={generating}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center gap-2 mx-auto"
          >
            {generating ? (
              <>
                <FaSpinner className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FaShare />
                Generate Referral Link
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Has referral code
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <FaUsers className="text-xl" />
            <h3 className="font-bold text-lg">Your Referral Program</h3>
          </div>
          <button
            onClick={() => setShowStats(!showStats)}
            className="text-white/80 hover:text-white text-sm flex items-center gap-1"
          >
            <FaChartLine />
            {showStats ? 'Hide Stats' : 'View Stats'}
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-emerald-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-emerald-600">{referralData.referrals_count}</p>
            <p className="text-sm text-emerald-800">Successful Referrals</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{referralData.pending_referrals}</p>
            <p className="text-sm text-amber-800">Pending Approval</p>
          </div>
        </div>

        {/* Referral Link */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Referral Link
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralData.referral_link}
              readOnly
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
            />
            <button
              onClick={copyToClipboard}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition ${
                copied 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {copied ? (
                <>
                  <FaCheck />
                  Copied!
                </>
              ) : (
                <>
                  <FaCopy />
                  Copy
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Share this link with colleagues you'd like to invite to the network
          </p>
        </div>

        {/* Referred Professionals List */}
        {showStats && referralData.referred_professionals?.length > 0 && (
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Your Referrals</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {referralData.referred_professionals.map((prof, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{prof.name}</p>
                    <p className="text-sm text-gray-600">
                      {prof.professional_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1 text-sm ${
                    prof.status === 'active' ? 'text-emerald-600' : 'text-amber-600'
                  }`}>
                    {prof.status === 'active' ? (
                      <>
                        <FaCheckCircle />
                        Approved
                      </>
                    ) : (
                      <>
                        <FaClock />
                        Pending
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showStats && referralData.referred_professionals?.length === 0 && (
          <div className="border-t pt-4 text-center py-4">
            <p className="text-gray-500 text-sm">
              No referrals yet. Share your link to start building your network!
            </p>
          </div>
        )}

        {/* Share Options */}
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm font-medium text-gray-700 mb-2">Quick Share</p>
          <div className="flex gap-2">
            <a
              href={`mailto:?subject=Join Legacy Forge Professional Network&body=I'd like to invite you to join Legacy Forge's professional network. You can sign up using my referral link: ${encodeURIComponent(referralData.referral_link)}`}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-center hover:bg-gray-50"
            >
              📧 Email
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralData.referral_link)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-center hover:bg-gray-50"
            >
              💼 LinkedIn
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfessionalReferralCard;
